type TraceDetails = Record<string, unknown>

export interface PayloadProfile {
  bytes: number
  fieldCount: number
  rowCount: number
}

const PERF_FLAG = 'VITE_WORKFLOW_PERF_DEBUG'

export function isWorkflowPerfEnabled(): boolean {
  return import.meta.env[PERF_FLAG] === 'true'
}

export function startWorkflowMeasure(name: string, details?: TraceDetails): () => number {
  const enabled = isWorkflowPerfEnabled()
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now()
  if (enabled && typeof performance !== 'undefined' && performance.mark) {
    performance.mark(`${name}:start`)
  }

  return () => {
    const end = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const durationMs = end - start
    if (enabled) {
      if (typeof performance !== 'undefined' && performance.mark && performance.measure) {
        const endMark = `${name}:end`
        performance.mark(endMark)
        try {
          performance.measure(name, `${name}:start`, endMark)
        } catch {
          // ignore
        }
      }
      console.debug('[workflow:perf]', name, {
        durationMs: Math.round(durationMs * 10) / 10,
        atMs: Math.round(end * 10) / 10,
        ...(details || {}),
      })
    }
    return durationMs
  }
}

export function profilePayload(payload: unknown): PayloadProfile {
  const visited = new WeakSet<object>()
  let fieldCount = 0
  let rowCount = 0

  const walk = (value: unknown, key?: string): void => {
    if (value == null || typeof value !== 'object') return
    if (visited.has(value as object)) return
    visited.add(value as object)

    if (key === 'fields' && !Array.isArray(value)) {
      fieldCount += Object.keys(value as Record<string, unknown>).length
    }
    if (key === 'rows' && Array.isArray(value)) {
      rowCount += value.length
    }

    if (Array.isArray(value)) {
      value.forEach((item) => walk(item))
      return
    }

    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
      walk(childValue, childKey)
    }
  }

  walk(payload)

  let bytes = 0
  try {
    bytes = new Blob([JSON.stringify(payload ?? null)]).size
  } catch {
    bytes = 0
  }

  return { bytes, fieldCount, rowCount }
}
